import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { PricingCard } from '@/components/molecules';
import { Heading, Paragraph, Skeleton } from '@/components/atoms';
import { useBusinessSubscription, useSubscriptionPlans } from '@/api';
import { routes } from '@/lib/constants';
import { formatCurrency } from '@/lib/helpers/format-currency';
import {
  getBillingIntervalLabel,
  getPlanDisplayName,
  getPlanPlatformFeePercent,
  isFreePlan,
} from '@/lib/helpers/subscription-plan';
import type { BillingInterval, SubscriptionPlan } from '@/types';

const CURRENCY_CODE = 'NGN';

const INTERVAL_TABS: Array<{ label: string; value: BillingInterval }> = [
  { label: 'Monthly', value: 'monthly' },
  { label: 'Quarterly', value: 'quarterly' },
  { label: 'Annually', value: 'yearly' },
];

function sortPlans(a: SubscriptionPlan, b: SubscriptionPlan) {
  const order = [
    'freemium',
    'starter',
    'standard',
    'growth',
    'premium',
    'pro',
    'scale',
  ];
  const aIndex = order.indexOf(a.package);
  const bIndex = order.indexOf(b.package);
  if (aIndex !== bIndex) {
    return (aIndex === -1 ? 99 : aIndex) - (bIndex === -1 ? 99 : bIndex);
  }
  return Number.parseFloat(a.price) - Number.parseFloat(b.price);
}

export const PricingSection = () => {
  const navigate = useNavigate();
  const [interval, setInterval] = useState<BillingInterval>('monthly');
  const [salesEstimate, setSalesEstimate] = useState(500_000);

  const { data: plans, isLoading } = useSubscriptionPlans();
  const { data: subscription } = useBusinessSubscription();

  const currentPlanId = subscription?.subscriptionPlanId;
  const currentPackage = subscription?.subscriptionPlan?.package;

  const availableIntervals = useMemo(() => {
    if (!plans?.length) return INTERVAL_TABS;
    const present = new Set(plans.map((plan) => plan.billingInterval));
    const filtered = INTERVAL_TABS.filter((tab) => present.has(tab.value));
    return filtered.length > 0 ? filtered : INTERVAL_TABS;
  }, [plans]);

  const visiblePlans = useMemo(() => {
    if (!plans) return [];

    const currencyPlans = plans.filter(
      (plan) => plan.currencyCode === CURRENCY_CODE
    );

    const intervalPlans = currencyPlans
      .filter((plan) => plan.billingInterval === interval || isFreePlan(plan))
      .sort(sortPlans);

    // Prefer one free plan even if interval differs.
    const freePlan =
      intervalPlans.find((plan) => isFreePlan(plan)) ??
      currencyPlans.find((plan) => isFreePlan(plan));

    const paidPlans = intervalPlans.filter((plan) => !isFreePlan(plan));
    const unique = new Map<number, SubscriptionPlan>();

    if (freePlan) unique.set(freePlan.id, freePlan);
    paidPlans.forEach((plan) => unique.set(plan.id, plan));

    return [...unique.values()].sort(sortPlans);
  }, [interval, plans]);

  const entryPaidPlan = useMemo(
    () => visiblePlans.find((plan) => !isFreePlan(plan)),
    [visiblePlans]
  );

  const feeOnStarter = salesEstimate * 0.02;
  const entryPrice = entryPaidPlan ? Number.parseFloat(entryPaidPlan.price) : 0;
  const monthlySavings = Math.max(0, feeOnStarter - entryPrice);

  const handleSelectPlan = (planId: number) => {
    navigate(`${routes.billingSummary}?planId=${planId}`);
  };

  if (isLoading) {
    return (
      <div className="flex w-full flex-col gap-8">
        <div className="space-y-3 text-left">
          <Skeleton className="h-8 w-80" />
          <Skeleton className="h-16 w-full max-w-2xl" />
        </div>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
          {[1, 2, 3, 4].map((item) => (
            <Skeleton key={item} className="h-[28rem] w-full rounded-2xl" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="flex w-full flex-col gap-10">
      <div className="max-w-3xl space-y-3 text-left">
        <Heading size="2xl" className="tracking-tight">
          Simple pricing that grows with you
        </Heading>
        <Paragraph className="text-muted-foreground">
          Start free with everything you need. Upgrade when your sales tell you
          it&apos;s time, and your subscription replaces the platform fee
          entirely.
        </Paragraph>
      </div>

      {availableIntervals.length > 1 && (
        <div className="mx-auto flex w-fit gap-1 rounded-full border bg-muted/40 p-1">
          {availableIntervals.map((tab) => (
            <button
              key={tab.value}
              type="button"
              onClick={() => setInterval(tab.value)}
              className={`rounded-full px-4 py-2 text-sm transition-colors ${
                interval === tab.value
                  ? 'bg-white text-foreground shadow-sm'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      )}

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
        {visiblePlans.map((plan) => {
          const amount = Number.parseFloat(plan.price);
          const name = getPlanDisplayName(plan);
          const isCurrent =
            plan.id === currentPlanId ||
            (isFreePlan(plan) &&
              (!currentPackage ||
                currentPackage === 'freemium' ||
                currentPackage === 'starter'));

          return (
            <PricingCard
              key={plan.id}
              planId={plan.id}
              name={name}
              description={plan.description}
              amount={Number.isFinite(amount) ? amount : 0}
              features={plan.features ?? []}
              intervalLabel={getBillingIntervalLabel(plan.billingInterval)}
              platformFeePercent={getPlanPlatformFeePercent(plan)}
              currencyCode={plan.currencyCode}
              isRecommended={
                plan.package === 'standard' || plan.package === 'growth'
              }
              isPopular={plan.package === 'premium' || plan.package === 'pro'}
              isCurrent={isCurrent}
              onSelect={handleSelectPlan}
            />
          );
        })}
      </div>

      <div className="grid gap-4 rounded-2xl border bg-white p-6 lg:grid-cols-[1.2fr_0.8fr]">
        <div className="space-y-4">
          <Heading size="lg">See how much you save</Heading>
          <Paragraph className="text-sm text-muted-foreground">
            Drag to your monthly sales. On the free plan you pay 2%. On any paid
            plan, you pay 0%.
          </Paragraph>
          <div className="space-y-3">
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">Monthly sales</span>
              <span className="font-semibold">
                {formatCurrency(salesEstimate)}
              </span>
            </div>
            <input
              type="range"
              min={50_000}
              max={5_000_000}
              step={50_000}
              value={salesEstimate}
              onChange={(event) => setSalesEstimate(Number(event.target.value))}
              className="w-full accent-primary"
            />
          </div>
        </div>
        <div className="rounded-xl border bg-muted/20 p-4">
          <Paragraph className="text-sm text-muted-foreground">
            Estimated monthly fee on Starter
          </Paragraph>
          <Paragraph className="mt-1 text-2xl font-semibold">
            {formatCurrency(feeOnStarter)}
          </Paragraph>
          {entryPaidPlan && (
            <Paragraph className="mt-4 text-sm text-muted-foreground">
              With {getPlanDisplayName(entryPaidPlan)} at{' '}
              {formatCurrency(entryPrice)}/
              {getBillingIntervalLabel(entryPaidPlan.billingInterval)}, you
              {monthlySavings > 0
                ? ` save about ${formatCurrency(monthlySavings)} / month.`
                : ' start saving as your sales grow.'}
            </Paragraph>
          )}
        </div>
      </div>

      <div className="rounded-2xl border bg-emerald-50/60 p-6">
        <Paragraph className="text-xs font-semibold tracking-wide text-emerald-800 uppercase">
          Meet Seller Mate · Coming soon
        </Paragraph>
        <Heading size="lg" className="mt-2">
          Your AI business assistant
        </Heading>
        <Paragraph className="mt-2 max-w-3xl text-sm text-muted-foreground">
          Seller Mate works behind the scenes — advising on your business and
          helping you make smarter decisions. Available on paid plans when it
          launches.
        </Paragraph>
      </div>
    </div>
  );
};
